/**
 * PD-1322 (S24d) — turning the signed-note 409 into guidance.
 *
 * `packages/middleware/src/visit/signed-note-guard.ts` rejects a note-edit
 * write whose STORED `docStatus` is already signed, with a 409
 * (`ConflictException`), and `visit.service.ts` maps the `If-Match` precondition
 * failure to the same 409. With the addendum flow in place that response is
 * UNREACHABLE through the UI — a final note renders no Edit affordance at all
 * (`ProgressNoteViewer.tsx` gates Edit on `isPreliminary`) and offers
 * "Add addendum" instead.
 *
 * What is left is a genuine race: the note was locked in another tab, or by
 * another clinician, between this edit form opening and its save landing. That
 * must not surface as a raw conflict string — the doctor's work is still in the
 * form and the addendum flow is the way to keep it.
 *
 * Detection mirrors the existing 409 precedent at
 * `components/billing/useVisitBilling.ts` (status / statusCode), plus a message match
 * so the guard's own wording is recognised even when the transport flattens the
 * status.
 */

export const SIGNED_NOTE_CONFLICT_TITLE = 'This note was signed while you were editing'

export const SIGNED_NOTE_CONFLICT_MESSAGE =
  'A signed note is never overwritten, so this edit was not applied. Close the editor and use ' +
  '"Add addendum" on the note — your change is filed as a signed addendum appended to it.'

export function isSignedNoteConflict(error: unknown): boolean {
  const candidate = error as { status?: number; statusCode?: number; message?: string } | undefined
  if (candidate?.status === 409 || candidate?.statusCode === 409) return true
  const message = typeof candidate?.message === 'string' ? candidate.message : ''
  // The guard's message — "… is signed (docStatus 'final') and cannot be edited."
  return /is signed \(docStatus/.test(message)
}
